import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, NgZone } from '@angular/core';

const AVG_CHAR_PX = 9;
const MIN_LENGTH = 5;
const ELLIPSIS = '...';

@Directive({
  selector: '[phxMiddleTruncate]'
})
export class PhxMiddleTruncateDirective implements AfterViewInit, OnDestroy {
  @Input() set phxMiddleTruncate(value: string) {
    this.fullText = value || '';
    // Re-truncate if text changes after initial render
    if (this.initialized) {
      this.schedule();
    }
  }

  private fullText: string = '';
  private lastRendered: string = '';
  private length: number = MIN_LENGTH;
  private resizeObserver: ResizeObserver;
  private lastObservedWidth: number = 0;
  private rafId: number | null = null;
  private initialized: boolean = false;
  private initalRafId: number | null = null;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  public ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.initalRafId = requestAnimationFrame(() => {
        if (!this.el?.nativeElement) return;
        const width: number = Math.round(this.el.nativeElement.clientWidth);
        this.lastObservedWidth = width;
        this.updateLength(width);
        this.truncate();
        this.initialized = true;

        this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
          for (const entry of entries) {
            const newWidth: number = Math.round(entry.contentRect.width);
            if (newWidth !== this.lastObservedWidth) {
              this.lastObservedWidth = newWidth;
              this.schedule();
            }
          }
        });
        this.resizeObserver.observe(this.el.nativeElement);
      });
    });
  }

  private schedule(): void {
    if (this.rafId != null) return;
    this.rafId = requestAnimationFrame((): void => {
      this.rafId = null;
      this.updateLength(this.lastObservedWidth);
      this.truncate();
    });
  }

  private updateLength(width: number): void {
    this.length = Math.max(MIN_LENGTH, Math.floor(width / AVG_CHAR_PX));
  }

  private truncate(): void {
    const full: string = this.fullText;
    if (!full) {
      if (this.lastRendered !== '') this.setText('');
      return;
    }
    const maxLen: number = this.length;
    let out: string = full;
    if (full.length > maxLen) {
      const show: number = Math.max(0, maxLen - ELLIPSIS.length);
      const front: number = Math.ceil(show / 2);
      const back: number = Math.floor(show / 2);
      out = full.substring(0, front) + ELLIPSIS + full.substring(full.length - back);
    }
    if (out !== this.lastRendered) this.setText(out);
  }

  private setText(text: string): void {
    const el: HTMLElement = this.el.nativeElement;
    el.textContent = text;
    el.title = this.fullText;
    this.lastRendered = text;
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.initalRafId !== null) cancelAnimationFrame(this.initalRafId);
    this.resizeObserver?.disconnect();
  }
}
